import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useCallback } from "react";

export interface UseExclusiveActionReturn {
  runExclusive: <T>(action: () => Promise<T>) => Promise<T | undefined>;
  isPending: boolean;
  isUiBlocked: boolean;
}

/**
 * Provides an exclusive-action guard keyed by `actionKey`.
 *
 * While an action is in-flight a second call with the same key is ignored and
 * `undefined` is returned. Pair `isPending` and `isUiBlocked` with `isDisabled`
 * / `isLoading` on the corresponding submit button.
 *
 * @example
 * const { runExclusive, isPending, isUiBlocked } = useExclusiveAction("get-started:next");
 *
 * const handleSubmit = (e: FormEvent) => {
 *   e.preventDefault();
 *   void runExclusive(async () => {
 *     await submitGetStarted();
 *   });
 * };
 */
export const useExclusiveAction = (actionKey: string): UseExclusiveActionReturn => {
  const { runExclusive: runExclusiveGuard, isActionPending, isUiBlocked } = useInteractionGuard();

  const runExclusive = useCallback(
    <T>(action: () => Promise<T>): Promise<T | undefined> => {
      return runExclusiveGuard(actionKey, action);
    },
    [actionKey, runExclusiveGuard],
  );

  return {
    runExclusive,
    isPending: isActionPending(actionKey),
    isUiBlocked,
  };
};
